import { nanoid } from "nanoid";
import type {
  FpCodegenCommands,
  FpUserMessageCreate,
} from "../../../schemas/codegen";

export type ChatConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "error";

export type MessageHandler = (event: MessageEvent) => void;
export type StatusHandler = (status: ChatConnectionStatus) => void;
export type ErrorHandler = (error: Error) => void;

interface PendingMessage {
  data: FpCodegenCommands;
  timestamp: number;
  retries: number;
}

class ChatConnection {
  private ws: WebSocket | null = null;
  private chatId: string;
  private url: string;
  private status: ChatConnectionStatus = "disconnected";
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers = new Set<MessageHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private errorHandlers = new Set<ErrorHandler>();
  private pendingMessages: PendingMessage[] = [];
  private connectPromise: Promise<void> | null = null;
  private maxPendingMessages = 50;
  private pendingMessageTimeout = 30000; // 30 seconds

  constructor(chatId: string, baseUrl = "") {
    this.chatId = chatId;
    this.url = `${baseUrl}/api/chats/${chatId}/ws`;
  }

  private setStatus(status: ChatConnectionStatus) {
    if (this.status !== status) {
      this.status = status;
      for (const handler of this.statusHandlers) {
        try {
          handler(status);
        } catch (_error) {}
      }
    }
  }

  private processPendingMessages() {
    if (
      this.ws?.readyState === WebSocket.OPEN &&
      this.pendingMessages.length > 0
    ) {
      const messages = [...this.pendingMessages];
      this.pendingMessages = [];

      for (const message of messages) {
        try {
          this.ws.send(JSON.stringify(message.data));
        } catch (_error) {
          // Re-queue the message for later if retries available
          if (message.retries < 3) {
            this.pendingMessages.unshift({
              ...message,
              retries: message.retries + 1,
            });
          }
          break;
        }
      }
    }
  }

  private cleanupPendingMessages() {
    const now = Date.now();
    this.pendingMessages = this.pendingMessages.filter(
      (message) => now - message.timestamp < this.pendingMessageTimeout,
    );
  }

  private processError(error: Error) {
    this.setStatus("error");
    for (const handler of this.errorHandlers) {
      try {
        handler(error);
      } catch (_handlerError) {}
    }
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.setStatus("error");
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 10000);

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch((_error) => {});
    }, delay);
  }

  async connect(): Promise<void> {
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      try {
        this.setStatus("connecting");

        if (this.ws?.readyState === WebSocket.OPEN) {
          this.setStatus("connected");
          resolve();
          return;
        }

        // Close existing connection if any
        if (this.ws) {
          this.ws.close();
        }

        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          this.setStatus("connected");
          this.processPendingMessages();
          resolve();
        };

        this.ws.onmessage = (event) => {
          for (const handler of this.messageHandlers) {
            try {
              handler(event);
            } catch (_error) {}
          }
        };

        this.ws.onerror = (_event) => {
          this.handleReconnect();
          reject(
            new Error(`WebSocket connection failed for chat ${this.chatId}`),
          );
        };

        this.ws.onclose = (event) => {
          this.setStatus("disconnected");

          // Only attempt reconnect if it wasn't a clean close
          if (event.code !== 1000) {
            this.handleReconnect();
          }
        };
      } catch (error) {
        this.processError(error as Error);
        reject(error);
      }
    }).finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Manual disconnect");
      this.ws = null;
    }

    this.setStatus("disconnected");
    this.reconnectAttempts = 0;
  }

  send(data: FpCodegenCommands): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
        return true;
      } catch (_error) {
        return false;
      }
    } else {
      // Check if we've exceeded the max pending messages
      if (this.pendingMessages.length >= this.maxPendingMessages) {
        this.pendingMessages.shift();
      }

      // Queue the message for when connection is established
      this.pendingMessages.push({
        data,
        timestamp: Date.now(),
        retries: 0,
      });

      // Auto-connect if not already connecting
      if (this.status === "disconnected") {
        this.connect().catch((_error) => {});
      }

      return false; // Message was queued, not sent immediately
    }
  }

  // Event handler management
  addMessageHandler(handler: MessageHandler) {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler: MessageHandler) {
    this.messageHandlers.delete(handler);
  }

  addStatusHandler(handler: StatusHandler) {
    this.statusHandlers.add(handler);
  }

  removeStatusHandler(handler: StatusHandler) {
    this.statusHandlers.delete(handler);
  }

  addErrorHandler(handler: ErrorHandler) {
    this.errorHandlers.add(handler);
  }

  removeErrorHandler(handler: ErrorHandler) {
    this.errorHandlers.delete(handler);
  }

  // Status getters
  isConnected(): boolean {
    return (
      this.status === "connected" && this.ws?.readyState === WebSocket.OPEN
    );
  }

  getStatus(): ChatConnectionStatus {
    return this.status;
  }

  getPendingMessageCount(): number {
    return this.pendingMessages.length;
  }

  // Cleanup
  destroy() {
    this.disconnect();
    this.messageHandlers.clear();
    this.statusHandlers.clear();
    this.errorHandlers.clear();
    this.pendingMessages = [];
  }

  // Cleanup old pending messages (called periodically by the manager)
  cleanupExpiredMessages() {
    this.cleanupPendingMessages();
  }
}

class ChatWebSocketManager {
  private baseUrl: string;
  private connections = new Map<string, ChatConnection>();
  private cleanupInterval: ReturnType<typeof setInterval>;

  /**
   * @NOTE - As of writing, the `baseUrl` parameter is only used for testing purposes.
   */
  private constructor(baseUrl = "") {
    this.baseUrl = baseUrl;
    // Clean up old pending messages every minute
    this.cleanupInterval = setInterval(() => {
      for (const connection of this.connections.values()) {
        connection.cleanupExpiredMessages();
      }
    }, 60000);

    // Clean up on page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.disconnectAll();
      });
    }
  }

  /**
   * We only want one instance of the ChatWebSocketManager running in the app at a time.
   * So, this is a method to create the single instance for the app,
   * while also creating an instance for test runs.
   */
  static _internalCreate(baseUrl = ""): ChatWebSocketManager {
    return new ChatWebSocketManager(baseUrl);
  }

  getConnection(chatId: string): ChatConnection {
    let connection = this.connections.get(chatId);

    if (!connection) {
      connection = new ChatConnection(chatId, this.baseUrl);
      this.connections.set(chatId, connection);
    }

    return connection;
  }

  removeConnection(chatId: string) {
    const connection = this.connections.get(chatId);
    if (connection) {
      connection.destroy();
      this.connections.delete(chatId);
    }
  }

  disconnectAll() {
    for (const [_chatId, connection] of this.connections) {
      connection.destroy();
    }
    this.connections.clear();
  }

  getConnectionStatus(chatId: string): ChatConnectionStatus {
    const connection = this.connections.get(chatId);
    return connection?.getStatus() || "disconnected";
  }

  getAllConnections(): Map<string, ChatConnection> {
    return new Map(this.connections);
  }

  // Utility method to send a message with automatic connection management
  async sendMessage(chatId: string, content: string): Promise<boolean> {
    const connection = this.getConnection(chatId);

    const messageData: FpUserMessageCreate = {
      type: "user.message.create",
      messageId: nanoid(),
      content,
    };

    // If not connected, try to connect first
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (_error) {
        // Message will be queued if connection fails
      }
    }

    return connection.send(messageData);
  }

  // Cleanup
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.disconnectAll();
  }
}

// Export singleton instance
export const chatWebSocketManager = ChatWebSocketManager._internalCreate();

// Export types and classes for testing/extension
export { ChatConnection, ChatWebSocketManager };
